import React, { useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import { postToAPI, getFromAPI } from '../../Utils/utils';
import { useCustomer } from './getCustomerData';
import { toast } from "react-toastify";
import { 
  fetchBankData, 
  fetchCountryData, 
  fetchTaxData, 
  fetchCutsomerData, 
  fetchBranchData 
} from '../../Utils/function_list';
import FormInputs from '../../Utils/FormInputs';
import message from '../../Utils/lang';

export default function NewCustomer({ onSuccess }) {
  // State declarations for dropdown options
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [banks, setBanks] = useState([]);
  const [branchData, setBranchData] = useState([]);
  const [errors, setErrors] = useState({});

  // Customer context
  const { customerData, fetchCustomerData } = useCustomer();

  // Get branch ID from localStorage
  const loginBranchId = localStorage.getItem('createdBranchId');

  // State for branch input data
  const [branchInputData, setBranchInputData] = useState({
    branchCountryCode: '',
    branchStateCode: '',
    branchCityCode: ''
  });

  // Tax options configuration
  const [taxOptions] = useState([
    { id: 1, name: 'B2C', code: 'b2c' },
    { id: 2, name: 'B2B', code: 'b2b' },
    { id: 3, name: 'B2B-IGST', code: 'b2b-igst' },
    { id: 4, name: 'B2C-IGST', code: 'b2c-igst' }
  ]);

  // Initial form input data matching database fields
  const initialInputData = {
    name: '',
    email: '',
    phone_number2: '',
    gender: '',
    birth_date: '',
    anniversary_date: '',
    address: '',
    reffered_by_id: 0,
    gst: '',
    country: '',
    state: '',
    city: '',
    tax: 0,
    pan: '',
    opening_balance: 0.0,
    bank_name: 0,
    ifsc_code: '',
    account_number: '',
  };

  const [inputData, setInputData] = useState({ ...initialInputData });

  // Fetch initial data on component mount
  useEffect(() => {
    fetchBranchData(setBranchData);
    fetchCountryData(setCountries);
    fetchBankData(setBanks);
    fetchTaxData(setTaxes);
    fetchCutsomerData(setCustomers);
  }, []);

  // Handle branch data and initial tax calculation
  useEffect(() => {
    if (branchData.length) {
      const currentBranch = branchData.find(branch => branch.id === parseInt(loginBranchId, 10));
      if (currentBranch) {
        const { country, state, city } = currentBranch;
        setBranchInputData({
          branchCountryCode: country,
          branchStateCode: state,
          branchCityCode: city,
        });

        const initialTax = determineAppropriateTax(state, state, '');
        setInputData(prev => ({
          ...prev,
          country,
          state,
          city,
          tax: initialTax
        }));

        handleCountryChange({ target: { value: country } });
        handleStateChange({ target: { value: state } });
      }
    }
  }, [branchData, loginBranchId]);

  // Determine appropriate tax based on states and GST
  const determineAppropriateTax = (customerState, branchState, gstNumber) => {
    if (!customerState || !branchState) {
      return taxOptions.find(tax => tax.code === 'b2c')?.id || 1;
    }
    
    const isInterState = customerState !== branchState;
    const hasGST = gstNumber && gstNumber.length === 15;
    
    if (hasGST) {
      return isInterState 
        ? taxOptions.find(tax => tax.code === 'b2b-igst')?.id 
        : taxOptions.find(tax => tax.code === 'b2b')?.id;
    }
    return isInterState 
      ? taxOptions.find(tax => tax.code === 'b2c-igst')?.id 
      : taxOptions.find(tax => tax.code === 'b2c')?.id;
  };

  // Handle country selection change
  const handleCountryChange = async (e) => {
    const selectedCountry = e.target.value;
    setInputData(prev => ({ ...prev, country: selectedCountry }));

    try {
      const response = await getFromAPI(`FtsState/?country_code=${selectedCountry}`);
      setStates(response);
      setCities([]);
    } catch (error) {
      return null;
    }
  };

  // Handle state selection change
  const handleStateChange = async (e) => {
    const selectedState = e.target.value;
    const newTax = determineAppropriateTax(selectedState, branchInputData.branchStateCode, inputData.gst);

    setInputData(prev => ({
      ...prev,
      state: selectedState,
      tax: newTax
    }));

    try {
      const response = await getFromAPI(`FtsCity/?state_id=${selectedState}`);
      setCities(response);
      if (response?.length > 0) {
        setInputData(prev => ({
          ...prev,
          city: response[0].id
        }));
      }
    } catch (error) {
      return null;
    }
  };

  // Enhanced GST change handler
  const handleGSTChange = async (gstNumber) => {
    setInputData(prev => ({ ...prev, gst: gstNumber }));

    if (gstNumber.length === 15) {
      const stateCode = gstNumber.substring(0, 2);
      const panNumber = gstNumber.substring(2, 12);

      try {
        // Fetch state data based on GST state code
        const stateResponse = await getFromAPI(`FtsState/?state_id=${stateCode}`);
        
        if (stateResponse.length > 0) {
          const stateData = stateResponse[0];
          const newTax = determineAppropriateTax(stateData.state_code, branchInputData.branchStateCode, gstNumber);

          // Update input data with state and PAN
          setInputData(prev => ({
            ...prev,
            state: stateData.state_code,
            pan: panNumber,
            tax: newTax
          }));

          setStates([{
            state_id: stateData.state_code,
            state_name: stateData.state_name
          }]);

          // Fetch cities for the selected state
          const cityResponse = await getFromAPI(`FtsCity/?state_id=${stateData.state_code}`);
          setCities(cityResponse);
          
          if (cityResponse?.length > 0) {
            setInputData(prev => ({
              ...prev,
              city: cityResponse[0].id
            }));
          }
        } else {
          // Reset if state not found
          setInputData(prev => ({
            ...prev,
            state: '',
            pan: ''
          }));
        }
      } catch (error) {
        toast.error('Error processing GST number');
      }
    } else {
      // Reset state and PAN if GST is invalid
      const newTax = determineAppropriateTax(inputData.state, branchInputData.branchStateCode, '');
      setInputData(prev => ({
        ...prev,
        state: '',
        pan: '',
        tax: newTax
      }));
    }
  };

  // Handle form input changes
  const handleCustomerInputChange = (index, field, value) => {
    if (field === "gst") {
      handleGSTChange(value);
    } else {
      setInputData(prev => ({ ...prev, [field]: value }));
    }
  };

  // Handle form submission
  const handleCustomerSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!inputData.name?.trim()) {
      toast.error(message.customer.required_name);
      return;
    }

    if (!inputData.phone_number2 || inputData.phone_number2.length !== 10) {
      toast.error(message.customer.required_phone_number);
      return;
    }

    // Check for duplicate phone number
    if (customerData.some(customer => customer.phone_number2 === inputData.phone_number2)) {
      toast.error(message.customer.already_exist);
      return;
    }

    try {
      const response = await postToAPI("add_customer/", inputData);
      if (response.status) {
        toast.success(message.customer.add_customer);
        if (onSuccess && response.user_id) {
          onSuccess(response.user_id);
        }
        fetchCustomerData();
        setInputData({ ...initialInputData });
      } else {
        toast.error(message.customer.failed_to_add);
      }
    } catch (error) {
      toast.error(message.customer.failed_to_add);
    }
  };

  // Form configuration
  const formRows = [
    {
      columns: [
        { size: { md: 6, xxl: 4 }, label: 'Customer Name', type: 'text', name: 'name', id: 'name', placeholder: 'Enter Customer Name...', value: inputData.name, isRequired: true },
        { size: { md: 6, xxl: 4 }, label: 'Email Address', type: 'email', name: 'email', id: 'email', placeholder: 'Enter Email Address...', value: inputData.email },
        { size: { sm: 6, xxl: 4 }, label: 'Mobile Number', type: 'text', name: 'phone_number2', id: 'phone_number2', placeholder: 'Enter Mobile Number...', value: inputData.phone_number2, isRequired: true },
        { size: { sm: 6, xxl: 4 }, label: 'Gender', type: 'select', name: 'gender', id: 'gender', value: inputData.gender,
          options: [
            { value: '', label: 'Select Gender' },
            { value: 'Male', label: 'Male' },
            { value: 'Female', label: 'Female' },
            { value: 'Other', label: 'Other' },
          ]
        },
        { size: { sm: 6, xxl: 4 }, label: 'Birthday', type: 'date', name: 'birth_date', id: 'birth_date', placeholder: 'DD/MM/YYYY', value: inputData.birth_date },
        { size: { sm: 6, xxl: 4 }, label: 'Anniversary', type: 'date', name: 'anniversary_date', id: 'anniversary_date', placeholder: 'DD/MM/YYYY', value: inputData.anniversary_date },
        { size: { md: 12 }, label: 'Address', type: 'text', name: 'address', id: 'address', placeholder: 'Enter address...', value: inputData.address },
        { size: { sm: 6 }, label: 'Referred By', type: 'select', name: 'reffered_by_id', id: 'reffered_by_id', value: inputData.reffered_by_id,
          options: [
            { value: '', label: 'None' },
            ...customers.map(item => ({ value: item.userid, label: item.name }))
          ]
        },
        { size: { sm: 6 }, label: 'GST', type: 'text', name: 'gst', id: 'gst', placeholder: 'Enter GST number...', value: inputData.gst },
        { size: { sm: 6, xxl: 4 }, label: 'Country', type: 'select', name: 'country', id: 'country', value: inputData.country,
          options: [
            { value: '', label: 'Select Country' },
            ...countries.map(item => ({ value: item.calling_code, label: item.short_name }))
          ]
        },
        { size: { sm: 6, xxl: 4 }, label: 'State', type: 'select', name: 'state', id: 'state', value: inputData.state,
          options: [
            { value: '', label: 'Select State' },
            ...states.map(item => ({ value: item.state_code, label: item.state_name }))
          ]
        },
        { size: { sm: 6, xxl: 4 }, label: 'City', type: 'select', name: 'city', id: 'city', value: inputData.city,
          options: [
            { value: '', label: 'Select City' },
            ...cities.map(item => ({ value: item.id, label: item.city_name }))
          ]
        },
        { size: { sm: 6, xxl: 4 }, label: 'Tax', type: 'select', name: 'tax', id: 'tax', value: inputData.tax,
          options: [
            { value: '', label: 'Select Tax' },
            ...taxOptions.map(item => ({ value: item.id, label: item.name }))
          ]
        },
        { size: { md: 6, xxl: 4 }, label: 'PAN', type: 'text', name: 'pan', id: 'pan', placeholder: 'Enter PAN number...', value: inputData.pan },
        { size: { md: 6, xxl: 4 }, label: 'Opening Balance', type: 'text', name: 'opening_balance', id: 'opening_balance', placeholder: 'Enter opening balance...', value: inputData.opening_balance },
        { size: { md: 6, xxl: 4 }, label: 'Bank Name', type: 'select', name: 'bank_name', id: 'bank_name', value: inputData.bank_name,
          options: [
            { value: '', label: 'Select Bank Name' },
            ...banks.map(item => ({ value: item.id, label: item.name }))
          ]
        },
        { size: { md: 6, xxl: 4 }, label: 'IFSC Code', type: 'text', name: 'ifsc_code', id: 'ifsc_code', placeholder: 'Enter IFSC code...', value: inputData.ifsc_code },
        { size: { md: 6, xxl: 4 }, label: 'Account Number', type: 'text', name: 'account_number', id: 'account_number', placeholder: 'Enter Account Number...', value: inputData.account_number },
      ]
    }
  ];

  const formButtons = [
    { variant: "primary", text: "Submit", onClick: handleCustomerSubmit },
  ];

  return (
    <Card.Body className='pb-0'>
      <Card.Title>Add Customer</Card.Title>
      <FormInputs 
        Buttons={formButtons} 
        rows={formRows} 
        handleInputChange={handleCustomerInputChange} 
        handleSubmit={handleCustomerSubmit} 
      />
    </Card.Body>
  );
}